/**
 * Who may read and who may change each settings section (DEV-140, S5 of
 * docs/plans/settings-architecture.md), from the DEV-67 roles matrix
 * (docs/plans/roles-matrix.md).
 *
 * The one registry of settings sections. The back office builds its menu,
 * addresses and read-only pages from it; the API's path table and its exact
 * per-route check (apps/api/src/config/settings-routes.ts) read the same
 * lists, so a section's access changes in exactly one place.
 *
 * Rules (Michael, 2026-10-18: follow the DEV-67 matrix):
 *  - Pricing: Admin and Client manager change; AML / MLRO, Finance and Support read.
 *  - Treasury: Admin changes; AML / MLRO, Client manager and Finance read
 *    (row 11 shows commissions to them; the client-money position is closed
 *    to Support, DEV-67 review).
 *  - Compliance: Admin and AML / MLRO, read and change (row 18).
 *  - Organisation, Access, Connections, Notifications: Admin only (row 19).
 *  - Finance never changes anything. A new section is closed to every role
 *    but Admin until somebody lists it.
 */

/** A back-office staff role as the API stores it. The console uses the lower-case form. */
export type StaffRole = 'ADMIN' | 'COMPLIANCE' | 'CLIENT_MANAGER' | 'FINANCE' | 'SUPPORT';

export const STAFF_ROLES: readonly StaffRole[] = ['ADMIN', 'COMPLIANCE', 'CLIENT_MANAGER', 'FINANCE', 'SUPPORT'];

const A: StaffRole = 'ADMIN';
const M: StaffRole = 'COMPLIANCE';
const C: StaffRole = 'CLIENT_MANAGER';
const F: StaffRole = 'FINANCE';
const S: StaffRole = 'SUPPORT';

export type SettingsGroupId =
  | 'organisation'
  | 'access'
  | 'connections'
  | 'treasury'
  | 'pricing'
  | 'compliance'
  | 'notifications';

export interface SettingsGroupDefinition {
  readonly id: SettingsGroupId;
  readonly label: string;
}

/** In menu order. */
export const SETTINGS_GROUPS: readonly SettingsGroupDefinition[] = [
  { id: 'organisation', label: 'Organisation' },
  { id: 'access', label: 'Access' },
  { id: 'connections', label: 'Connections' },
  { id: 'treasury', label: 'Treasury' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'notifications', label: 'Notifications' },
];

/** A part of a section whose changes are narrower than the section's (the matrix names it on its own). */
export interface SettingsPart {
  readonly id: string;
  readonly label: string;
  readonly write: readonly StaffRole[];
}

export interface SettingsSectionDefinition {
  readonly group: SettingsGroupId;
  readonly id: string;
  readonly label: string;
  /** Who may open the section and read what it holds, its History included. */
  readonly read: readonly StaffRole[];
  /** Who may change it. Always a subset of `read`. */
  readonly write: readonly StaffRole[];
  /**
   * Who may read the current VALUE through the API, without the page or its
   * History: a setting other screens depend on (DEV-162, Currencies offered
   * fills every staff role's currency pickers). Always a superset of `read`;
   * absent = `read`.
   */
  readonly valueReaders?: readonly StaffRole[];
  /** The address this page had as a tab (DEV-132), redirected here. */
  readonly legacyId?: string;
  /** The `subjectType`s of GET /admin/settings/history this section's History reads. */
  readonly historySubjects?: readonly string[];
  readonly parts?: readonly SettingsPart[];
  /** Whether "Restore this version" is offered from the History, and who must agree (DEV-142). */
  readonly restore: SettingsRestorePolicy;
}

/**
 * Restoring an earlier version from a section's History (DEV-142). A restore
 * is a new change, never an edit of history: it goes through the save bar,
 * needs a reason and is recorded as a new version. The server restores from
 * the version it stored, never from values the console sends.
 *
 *  - `single`: one of the section's writers restores it.
 *  - `second-approver`: one writer proposes it with a reason; a DIFFERENT
 *    writer approves it, and only then does it take effect (four eyes). Both
 *    are recorded in the History.
 *  - not offered: `why` is what the page says instead.
 */
export type SettingsRestorePolicy =
  | { readonly offered: true; readonly approval: 'single' | 'second-approver' }
  | { readonly offered: false; readonly why: string };

export const RESTORE_SINGLE: SettingsRestorePolicy = { offered: true, approval: 'single' };
/** Four eyes on restore: available per section, off everywhere by decision 18.10 (one approver). */
export const RESTORE_FOUR_EYES: SettingsRestorePolicy = { offered: true, approval: 'second-approver' };
const noRestore = (why: string): SettingsRestorePolicy => ({ offered: false, why });

const ADMIN_ONLY: readonly StaffRole[] = [A];
const COMPLIANCE: readonly StaffRole[] = [A, M];
const TREASURY_READERS: readonly StaffRole[] = [A, M, C, F];

/** In menu order within each group. */
export const SETTINGS_SECTIONS: readonly SettingsSectionDefinition[] = [
  // DEV-138: who Norvix is, printed on statements, confirmations and security notices.
  { group: 'organisation', id: 'company', label: 'Company details', read: ADMIN_ONLY, write: ADMIN_ONLY, historySubjects: ['ORGANISATION'],
    restore: RESTORE_SINGLE },
  // DEV-162: the currencies accounts are opened in. Changed by Admin (row 19);
  // its value fills every staff role's currency pickers, so all may read the value.
  { group: 'organisation', id: 'currencies', label: 'Currencies offered', read: ADMIN_ONLY, write: ADMIN_ONLY,
    valueReaders: STAFF_ROLES, historySubjects: ['CURRENCIES'], restore: RESTORE_SINGLE },
  { group: 'access', id: 'admin-users', label: 'Admin users', read: ADMIN_ONLY, write: ADMIN_ONLY, legacyId: 'admin-users', historySubjects: ['ADMIN_USERS'],
    restore: noRestore('Admin-user changes are actions on staff accounts, not settings versions, so there is nothing to restore.') },
  { group: 'connections', id: 'providers', label: 'Providers', read: ADMIN_ONLY, write: ADMIN_ONLY, legacyId: 'integrations', historySubjects: ['INTEGRATION'],
    restore: noRestore('A provider connection is not restored from the History: its credentials are never kept there.') },
  // DEV-139: the client-money and shared accounts, moved off the provider cards.
  { group: 'treasury', id: 'accounts', label: 'Accounts', read: TREASURY_READERS, write: ADMIN_ONLY, historySubjects: ['TREASURY'],
    restore: RESTORE_SINGLE },
  { group: 'treasury', id: 'commission-transfer', label: 'Commission transfer', read: TREASURY_READERS, write: ADMIN_ONLY, historySubjects: ['TREASURY'],
    restore: RESTORE_SINGLE },
  {
    group: 'pricing', id: 'fees', label: 'Fees and provider costs', read: [A, M, C, F, S], write: [A, C], legacyId: 'fees',
    // DEV-67 row 11 and its review: what GuruPay charges us is changed by Admin only.
    parts: [{ id: 'provider-costs', label: 'Provider costs', write: ADMIN_ONLY }],
    // Prices are already versions with effective dates: a restore publishes
    // the old price again as a new version, effective now.
    restore: RESTORE_SINGLE,
  },
  // Versions are stored already: a restore makes an older one active again.
  { group: 'compliance', id: 'onboarding-policy', label: 'Onboarding policy', read: COMPLIANCE, write: COMPLIANCE, legacyId: 'onboarding-policy', historySubjects: ['ONBOARDING_POLICY'],
    restore: RESTORE_SINGLE },
  // A restore here changes who gets flagged. Four eyes is available
  // (RESTORE_FOUR_EYES) but off by decision (Michael, 18.10): one approver,
  // as everywhere. Switch it on here when compliance requires it.
  { group: 'compliance', id: 'transaction-monitoring', label: 'Transaction monitoring', read: COMPLIANCE, write: COMPLIANCE, legacyId: 'transaction-monitoring', historySubjects: ['MONITORING_RULES'],
    restore: RESTORE_SINGLE },
  { group: 'compliance', id: 'risk-scoring', label: 'Risk scoring', read: COMPLIANCE, write: COMPLIANCE, legacyId: 'risk-scoring', historySubjects: ['RISK_SCORING'],
    restore: RESTORE_SINGLE },
  { group: 'notifications', id: 'alerts', label: 'Alert routing', read: ADMIN_ONLY, write: ADMIN_ONLY, legacyId: 'alerts', historySubjects: ['ALERT_ROUTING'],
    restore: noRestore('Alert routing keeps no versions to go back to; change the routes directly.') },
];

/** `group.id`, e.g. `treasury.accounts`. */
export type SettingsSectionKey = `${SettingsGroupId}.${string}`;

export type SettingsAccessMode = 'read' | 'write';

export const settingsSectionKey = (section: Pick<SettingsSectionDefinition, 'group' | 'id'>): SettingsSectionKey =>
  `${section.group}.${section.id}`;

export function findSettingsSection(key: string): SettingsSectionDefinition | undefined {
  return SETTINGS_SECTIONS.find((section) => settingsSectionKey(section) === key);
}

function sectionOrThrow(key: string): SettingsSectionDefinition {
  const section = findSettingsSection(key);
  if (section === undefined) throw new Error(`Unknown settings section "${key}"`);
  return section;
}

/** The roles a section admits for reading or for changing. Throws for a section nobody defined. */
export function settingsRoles(key: string, mode: SettingsAccessMode): readonly StaffRole[] {
  const section = sectionOrThrow(key);
  return mode === 'read' ? section.read : section.write;
}

/** Who may read the section's current value through the API (DEV-162): `valueReaders`, else `read`. */
export function settingsValueReaders(key: string): readonly StaffRole[] {
  const section = sectionOrThrow(key);
  return section.valueReaders ?? section.read;
}

/** Who may change one part of a section (narrower than the section, e.g. provider costs). */
export function settingsPartWriters(key: string, partId: string): readonly StaffRole[] {
  const part = sectionOrThrow(key).parts?.find((p) => p.id === partId);
  if (part === undefined) throw new Error(`Unknown part "${partId}" of settings section "${key}"`);
  return part.write;
}

/**
 * The roles that hold the permission on EVERY listed section, for an endpoint
 * that serves more than one (the Treasury endpoints serve Accounts and
 * Commission transfer).
 */
export function settingsRolesForAll(keys: readonly string[], mode: SettingsAccessMode): readonly StaffRole[] {
  if (keys.length === 0) throw new Error('An endpoint must name at least one settings section');
  return STAFF_ROLES.filter((role) => keys.every((key) => settingsRoles(key, mode).includes(role)));
}

/** True when `role` (either case; unknown = nothing) holds the permission on the section. */
export function maySettings(role: string | null | undefined, key: string, mode: SettingsAccessMode): boolean {
  if (role === null || role === undefined) return false;
  return (settingsRoles(key, mode) as readonly string[]).includes(role.toUpperCase());
}

/** The sections whose History reads `subjectType`. */
export function sectionsWithHistorySubject(subjectType: string): readonly SettingsSectionDefinition[] {
  return SETTINGS_SECTIONS.filter((section) => section.historySubjects?.includes(subjectType) === true);
}

/** Who may read the History of `subjectType`: anyone who may read a section showing it. Empty when no section does. */
export function historySubjectReaders(subjectType: string): readonly StaffRole[] {
  const sections = sectionsWithHistorySubject(subjectType);
  return STAFF_ROLES.filter((role) => sections.some((section) => section.read.includes(role)));
}

/** The section's restore policy (DEV-142). Throws for a section nobody defined. */
export function settingsRestorePolicy(key: string): SettingsRestorePolicy {
  return sectionOrThrow(key).restore;
}

/**
 * True when `role` may restore a version of the section (for four eyes: may
 * propose one, and approve or reject one somebody else proposed): one of the
 * section's writers, where restore is offered.
 */
export function mayRestoreSettings(role: string | null | undefined, key: string): boolean {
  return settingsRestorePolicy(key).offered && maySettings(role, key, 'write');
}

/** True when a restore of the section takes effect only once a second writer approves it. */
export function restoreNeedsSecondApprover(key: string): boolean {
  const policy = settingsRestorePolicy(key);
  return policy.offered && policy.approval === 'second-approver';
}

/** Everyone who may read some section's History: what the History path admits before the handler narrows it. */
export function anyHistoryReaders(): readonly StaffRole[] {
  return STAFF_ROLES.filter((role) => SETTINGS_SECTIONS.some((s) => (s.historySubjects?.length ?? 0) > 0 && s.read.includes(role)));
}
